import React, { Suspense, useEffect, useState, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import type { ReactNode } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import DashboardSidebar from "@/components/DashboardSidebar";
import MotivationalModal from "@/components/MotivationalModal";
import { useGlobal } from "@/context/GlobalContext";
import { EnrollmentStepsProvider } from "@/context/EnrollmentStepsContext";
import BrandingLogo from "@/components/BrandingLogo";
import LeftOffRedirector from "@/components/LeftOffRedirector";
import { mapUserToForms, isProfileComplete } from "@/core/mappers/userMapper";
import { GoBell } from "react-icons/go";
import Loading from "@/components/Shared/Loading";
import GodModeToggle from "@/components/Dev/GodModeToggle";
import { isDevToolsEnabled } from "@/utils/devTools";
export interface DashboardLayoutProps {
    children: ReactNode;
}

interface LocationState {
    showMotivationalModal?: boolean;
    showVerificationModal?: boolean;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
    const location = useLocation();
    const [showMotivationalModal, setShowMotivationalModal] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const {
        getUser,
        getIsVerified,
        logout,
        updateFirstVisit,
        refreshUser,
        getHasDashboardLayoutRun,
        setHasDashboardLayoutRun,
        godMode,
    } = useGlobal();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const navigate = useNavigate();

    const user = getUser();
    const mappedUser = mapUserToForms(user);
    const profileComplete = isProfileComplete(mappedUser);

    const hasDashboardLayoutRun= getHasDashboardLayoutRun();
    const isVerified = getIsVerified();
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const [menuPos, setMenuPos] = useState<{
        top: number;
        left: number;
    } | null>(null);

    useLayoutEffect(() => {
        if (userMenuOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const top = rect.bottom + window.scrollY + 4;
            const left = Math.max(rect.right + window.scrollX - 288, 8);
            setMenuPos({ top, left });
        }
    }, [userMenuOpen]);

    useEffect(() => {
        const handleResize = () => {
            if (userMenuOpen && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                const top = rect.bottom + window.scrollY + 4;
                const left = Math.max(rect.right + window.scrollX - 288, 8);
                setMenuPos({ top, left });
            }
        };
        window.addEventListener("resize", handleResize);
        window.addEventListener("scroll", handleResize, true);
        return () => {
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("scroll", handleResize, true);
        };
    }, [userMenuOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (userMenuOpen) {
                if (triggerRef.current && (target === triggerRef.current || triggerRef.current.contains(target))) return;
                const portalMenu = document.getElementById("user-menu-portal");
                if (portalMenu && portalMenu.contains(target)) return;
                setUserMenuOpen(false);
            }
        };
        // Escape closes the menu too, and returns focus to the button that opened it.
        const handleKeyDown = (event: KeyboardEvent) => {
            if (userMenuOpen && event.key === "Escape") {
                setUserMenuOpen(false);
                triggerRef.current?.focus();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [userMenuOpen]);

    const handleRefreshUser = async () => {
        try {
            await refreshUser();
        } catch (error) {
            console.error("Failed to refresh user:", error);
        }
    };

    // Whether this render must be replaced with a redirect to /dashboard/profile: verified user,
    // profile incomplete, not god mode, and not already on one of the two pages this state is
    // allowed to render (the profile flow itself, and admissions-interview which sits ahead of it
    // in the journey). Computed synchronously (not just inside the effect below) so the gated
    // route's content -- and the sidebar showing links to pages the applicant can't reach yet --
    // never paints at all. Previously this was decided only inside a useEffect, which runs after
    // the initial paint: the real page rendered for one frame, sidebar included, before
    // navigate() swapped it out.
    const requiresProfileCompletionRedirect =
        !!user && isVerified && !godMode && !profileComplete &&
        !location.pathname.startsWith("/dashboard/profile") &&
        !location.pathname.startsWith("/dashboard/admissions-interview");

    // Always check profile completeness when dashboard layout mounts or when path changes.
    useEffect(() => {
        if (requiresProfileCompletionRedirect) {
            navigate("/dashboard/profile", { replace: true });
        }
    }, [requiresProfileCompletionRedirect, navigate]);

    useEffect(() => {
        if (hasDashboardLayoutRun) return;
        setHasDashboardLayoutRun(true);
        if (!isVerified) {
            navigate("/", { replace: true });
            return;
        }
        if (godMode) return;
        const allowedFirstVisitPages = [
            "/dashboard/financial",
            "/dashboard/documents",
            "/dashboard/program-enrollment",
        ];
        if (user?.firstVisit && !location.pathname.startsWith("/dashboard/profile") && !allowedFirstVisitPages.includes(location.pathname)) {
            navigate("/dashboard/profile");
            setShowMotivationalModal(true);
            return;
        }
        const locationState = location.state as LocationState;
        if (locationState?.showMotivationalModal) setShowMotivationalModal(true);
    }, [
        isVerified,
        user,
        location.state,
        location.pathname,
        navigate,
        hasDashboardLayoutRun,
        setHasDashboardLayoutRun,
        godMode,
    ]);

    // If user is not verified, don't render the dashboard layout
    if (!isVerified) {
       /* return <DashboardLoading />;*/
    }

    // Render the loading state instead of the real layout/sidebar/page while a redirect to
    // /dashboard/profile is pending. All hooks above have already run unconditionally, so this
    // early return is safe; it only affects what gets painted.
    if (requiresProfileCompletionRedirect) {
        return <Loading message="Loading..." fullscreen />;
    }

    const handleCloseMotivationalModal = async () => {
        try {
            await updateFirstVisit();
            await handleRefreshUser();
        } catch (e) {
            console.error(e);
        } finally {
            setShowMotivationalModal(false);
        }
    };

    const handleLogout = async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        try {
            await logout();
        } catch (e) {
            console.error("Logout error:", e);
        } finally {
            setIsLoggingOut(false);
        }
    };

    const toggleUserMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setUserMenuOpen((o) => !o);
    };
    const toggleMobileMenu = () => setIsMobileMenuOpen((o) => !o);

    const getUserDisplayName = () => {
        if (user?.firstName && user?.lastName) return `${user.firstName} ${user.lastName}`;
        if (user?.name) return user.name;
        if (user?.firstName) return user.firstName;
        return "User";
    };
    const getUserEmail = () => user?.email || "user@university.edu";
    const getStudentNumber = () => user?.studentNumber || user?.studentId || "STU123456";

    // build portal content separately to keep JSX clean
    const userMenuPortal =
        userMenuOpen && menuPos
            ? createPortal(
                <div
                    id="user-menu-portal"
                    style={{
                        position: "absolute",
                        top: menuPos.top,
                        left: menuPos.left,
                        zIndex: 10050,
                        width: "18rem",
                    }}
                    className="bg-slate-900/95 border-slate-800 rounded-xl border font-sora shadow-2xl backdrop-blur-md"
                >
                    <div className="border-slate-800 border-b px-4 py-3">
                        <div className="flex items-center">
                            <div className="from-ecpi-blue to-ecpi-electric text-white flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br font-medium shadow">
                                {getUserDisplayName().charAt(0).toUpperCase()}
                            </div>
                            <div className="ml-3 min-w-0 flex-1">
                                <div className="text-white truncate text-sm font-medium">
                                    {getUserDisplayName()}
                                </div>
                                <div className="text-slate-400 truncate text-sm">
                                    {getUserEmail()}
                                </div>
                                <div className="text-slate-500 text-xs">
                                    Student ID: {getStudentNumber()}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="py-1">
                        <a
                            href="/dashboard/profile?step=personal"
                            className="text-slate-300 flex items-center px-4 py-2 text-sm transition hover:bg-slate-800/50 hover:text-white"
                            onClick={(e) => {
                                e.preventDefault();
                                setUserMenuOpen(false);
                                navigate("/dashboard/profile?step=personal");
                            }}
                        >
                            <svg
                                className="text-slate-400 mr-3 h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                />
                            </svg>
                            My Profile
                        </a>
                        {/* Settings link temporarily removed for MVP (target page is not yet built).
                            To reinstate, change the `false` guard below to `true` (or remove the wrapper). */}
                        {false && (
                        <a
                            href="/dashboard/settings"
                            className="text-slate-300 flex items-center px-4 py-2 text-sm transition hover:bg-slate-800/50 hover:text-white"
                            onClick={() => setUserMenuOpen(false)}
                        >
                            <svg
                                className="text-slate-400 mr-3 h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                                />
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                            </svg>
                            Settings
                        </a>
                        )}
                        {/* Notifications link hidden: there is no notifications page or route (NotificationsPage is
                            disabled), and the plain href left the app without the path prefix. To reinstate, build the
                            page, add its route, use navigate() like My Profile, and change the `false` guard. */}
                        {false && (
                        <a
                            href="/dashboard/notifications"
                            className="text-slate-300 flex items-center px-4 py-2 text-sm transition hover:bg-slate-800/50 hover:text-white"
                            onClick={() => setUserMenuOpen(false)}
                        >
                            <svg
                                className="text-slate-400 mr-3 h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 17h5l-5 5-5-5h5V3h-5l5-5 5 5h-5v14z"
                                />
                            </svg>
                            Notifications
                        </a>
                        )}
                    </div>
                    <div className="border-slate-800 border-t">
                        <button
                            type="button"
                            className="text-red-400 flex w-full items-center px-4 py-2 text-sm transition hover:bg-red-900/20 disabled:opacity-50"
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                        >
                            {isLoggingOut ? (
                                <>
                                    <div className="border-slate-600 border-t-white mr-3 h-4 w-4 animate-spin rounded-full border-2" />
                                    Signing Out...
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="text-red-400 mr-3 h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                                        />
                                    </svg>
                                    Sign Out
                                </>
                            )}
                        </button>
                    </div>
                </div>,
                document.body
            )
            : null;
    // const ynsDocuments = getSectionDetail(SectionId.Documents); // TEMP: Documents header link hidden
    return (
        <EnrollmentStepsProvider>
            <div className="flex h-dvh flex-col overflow-hidden">
            {/* Animated background layer - full screen, fixed, lowest z-index */}
            {/* Branding Logo above sidebar */}
            <div className="bg-ecpi-neutral bg-ecpi-neutral-text flex w-full flex-shrink-0">
                <LeftOffRedirector />
                <div className="flex w-0 flex-shrink-0 flex-col items-center pt-4 lg:w-[270px]">
                    {/* In-app link: the plain href to the site root reloaded the whole app (auth check, full-screen
                        loader, landing page, then the redirect back to the dashboard). */}
                    <Link
                        to="/dashboard"
                        aria-label="ECPI University - Dashboard"
                        className="rounded-md transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ecpi-blue/50 focus-visible:outline-none"
                    >
                        <BrandingLogo
                            white={false}
                            width="150px"
                            className="mb-4 hidden lg:block"
                        />
                    </Link>
                </div>
                <div className="flex-1">
                    <header className="relative z-30 w-full flex-shrink-0 px-4 py-3 backdrop-blur-md">
                        <div className="flex max-w-full items-center justify-between">
                            {/* left side */}
                            <div className="flex items-center gap-4">
                                {/* Sidebar toggle only */}
                                <button
                                    type="button"
                                    className="rounded-md p-2 transition hover:bg-[#0054c71a] focus:ring-ecpi-blue/30 focus:outline-none focus:ring-2 lg:hidden"
                                    onClick={toggleMobileMenu}
                                    aria-expanded={isMobileMenuOpen}
                                >
                                    <span className="sr-only">
                                        Open sidebar
                                    </span>
                                    <svg
                                        className="h-6 w-6"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M4 6h16M4 12h16M4 18h16"
                                        />
                                    </svg>
                                </button>
                            </div>
                            {/* center */}
                            <div className="mx-8 flex-1 text-left">
                                <h2 className="font-sora">
                                    Student Enrollment Portal
                                </h2>

                            </div>
                            {/* TEMP: Documents header link hidden
                            <div className={"flex" }>
                                <ynsDocuments.icon className={"h-[16px] w-[16px]" } /> 
                                <span className={"ml-[5px]" }>Documents </span>
                            </div>
                            */}
                            {/* Notification bell hidden: it was a bare icon with no action, list or label, so it read as a
                                broken control. Reinstate it (as a button with an aria-label) once notifications exist. */}
                            {false && (
                            <div className={"h-full flex items-center p-5"}>
                                <GoBell />
                            </div>
                            )}
                            {/* user button */}
                            <div className="user-dropdown">
                                <button
                                    ref={triggerRef}
                                    type="button"
                                    className="flex items-center rounded-lg border px-2 py-1 text-sm transition hover:bg-[#0054c71a] focus:ring-ecpi-blue/30 focus:outline-none focus:ring-2"
                                    onClick={toggleUserMenu}
                                    disabled={isLoggingOut}
                                    aria-haspopup="true"
                                    aria-expanded={userMenuOpen}
                                    aria-label="Account menu"
                                >
                                    <div className="flex items-center">
                                        <div className="bg-ecpi-blue h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br text-sm font-medium shadow-sm">
                                            {getUserDisplayName().charAt(0).toUpperCase()}
                                        </div>
                                        <div className="ml-3 hidden text-left md:block">
                                            <div className=" max-w-32 truncate text-sm font-medium">
                                                {getUserDisplayName()}
                                            </div>
                                            <div className="text-slate-400 max-w-32 truncate text-xs">
                                                {getUserEmail()}
                                            </div>
                                        </div>
                                        <svg
                                            className="text-slate-400 ml-2 h-4 w-4"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M19 9l-7 7-7-7"
                                            />
                                        </svg>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </header>
                </div>
            </div>

            <div className="flex flex-1 min-h-0 overflow-hidden">
                {showMotivationalModal && (
                    <MotivationalModal
                        onClose={handleCloseMotivationalModal}
                        // The modal marks the first visit complete itself on its last step; closing through onClose
                        // repeated both requests while the faded-out overlay still covered the page.
                        onComplete={() => setShowMotivationalModal(false)}
                    />
                )}
                {
                    location.pathname !== "/dashboard" &&
                    <DashboardSidebar
                        isOpen={isMobileMenuOpen}
                        onClose={() => setIsMobileMenuOpen(false)}
                        profileComplete={profileComplete}
                    />
                }
                {isMobileMenuOpen && (
                    <div
                        className="bg-black/30 fixed inset-0 z-40 lg:hidden"
                        onClick={() => setIsMobileMenuOpen(false)}
                    />
                )}

                <div className="bg-ecpi-bg-content flex min-h-0 min-w-0 flex-1 flex-col">
                    <main className="flex-1 min-h-0 overflow-y-auto">
                        <div className="max-w-7xl p-2 sm:p-4 mx-auto">
                            {/* Pages are lazy-loaded. Without a boundary here a loading page suspended to the app-level
                                one, which swapped the whole layout for a full-screen loader. */}
                            <Suspense fallback={<Loading message="Loading page..." />}>
                                {children}
                            </Suspense>
                        </div>
                    </main>
                </div>
            </div>
            </div>

            {userMenuPortal}

            {isDevToolsEnabled() && <GodModeToggle />}
        </EnrollmentStepsProvider>
    );
};

export default DashboardLayout;
